class ScrollBanner extends HTMLElement {
  constructor() {
    super();
    this.animation = null;
    this.initialized = false;
  }

  connectedCallback() {
    if (!this.initialized) {
      this.initialized = true;
      this.init();
      this.setupResizeObserver();
      this.setupHoverEvents();
    }
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.animation?.cancel();
    this.initialized = false;
  }

  static get observedAttributes() {
    return ['text', 'speed', 'divider-image'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (this.isConnected && oldValue !== newValue) {
      this.init();
    }
  }

  init() {
    const text = this.getAttribute('text') || '';
    if (!text.trim()) return;

    this.createScrollContent(text);
    this.setupAnimation();
  }

  createScrollContent(text) {
    this.innerHTML = `<div class="scroll-wrapper" role="marquee" aria-live="off"><div class="scroll-content"></div></div>`;
    
    const scrollContent = this.querySelector('.scroll-content');
    const items = text.split(',').map(item => item.trim()).filter(Boolean);
    const dividerImage = this.getAttribute('divider-image');
    
    const createItemSet = () => {
      const fragment = document.createDocumentFragment();
      items.forEach((item, index) => {
        const textEl = document.createElement('span');
        textEl.className = 'scroll-item';
        textEl.textContent = item;
        fragment.appendChild(textEl);
        
        if (index < items.length - 1 && dividerImage) {
          const separator = document.createElement('span');
          separator.className = 'scroll-separator';
          separator.innerHTML = `<img src="${dividerImage}" alt="" class="divider-icon">`;
          fragment.appendChild(separator);
        }
      });
      return fragment;
    };
    
    // Measure one set to calculate how many we need
    const measureSet = createItemSet();
    scrollContent.appendChild(measureSet);
    
    const wrapperWidth = this.offsetWidth || window.innerWidth;
    const setsNeeded = Math.max(3, Math.ceil((wrapperWidth * 3) / scrollContent.scrollWidth));
    
    scrollContent.innerHTML = '';
    
    for (let setIndex = 0; setIndex < setsNeeded; setIndex++) {
      const itemSet = createItemSet();
      Array.from(itemSet.children).forEach((child, childIndex) => {
        child.setAttribute('aria-hidden', setIndex === 0 && childIndex === 0 ? 'false' : 'true');
      });
      scrollContent.appendChild(itemSet);
    }
  }

  setupAnimation() {
    const scrollContent = this.querySelector('.scroll-content');
    if (!scrollContent) return;

    this.animation?.cancel();
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const items = this.getAttribute('text').split(',').filter(Boolean);
        const dividerImage = this.getAttribute('divider-image');
        const elementsPerSet = items.length + (dividerImage ? items.length - 1 : 0);
        const totalSets = Math.floor(scrollContent.children.length / elementsPerSet);
        const scrollDistance = Math.round(scrollContent.scrollWidth / totalSets);
        
        this.animation = scrollContent.animate([
          { transform: 'translateX(0)' },
          { transform: `translateX(-${scrollDistance}px)` }
        ], {
          duration: (parseInt(this.getAttribute('speed')) || 30) * 1000,
          iterations: Infinity,
          easing: 'linear'
        });
      });
    });
  }

  setupHoverEvents() {
    this.addEventListener('mouseenter', () => this.animation?.pause());
    this.addEventListener('mouseleave', () => this.animation?.play());
  }

  setupResizeObserver() {
    if (!window.ResizeObserver) return;
    
    this.resizeObserver = new ResizeObserver(() => {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => this.init(), 250);
    });
    this.resizeObserver.observe(this);
  }
}

if (!customElements.get('scroll-banner')) {
  customElements.define('scroll-banner', ScrollBanner);
}